import { put, take, fork, call, select, all } from "redux-saga/effects";
import {
  enableScopeLoading,
  disableScopeLoading,
} from "../scope-loading/actions";
import { ScopeErrorAction } from "../scope-error/types";
import { UserAction, UserScope } from "./types";
import { RootState } from "../root-reducer";
import axios from "axios";
import * as Api from "../../service/api";
import {
  generateHTMLContent,
  mapApiClaim,
  mapApiContract,
  mapApiPendingContract,
  mapApiSkus,
} from "./helper";
import {
  setClaims,
  setContractRecords,
  setPendingContractRecords,
  setSkus,
} from "./actions";

function* submitTicketFlow() {
  while (true) {
    const { payload, callBack } = yield take(UserAction.SUBMIT_TICKET);
    yield put(enableScopeLoading(`contact-${UserScope}`));
    try {
      const dealerId = yield select(
        (state: RootState) =>
          state?.user?.selectedOrg?.associated_dealers?.[0]?.id
      );
      if (!dealerId) throw new Error("Dealer id not found.");

      const contact_email = yield select(
        (state: RootState) => state?.user?.email
      );
      let { data = {}, files } = payload;

      const uploadedUrls = yield all(
        files.map(function* (file: File) {
          const { url: uploadURL } = yield call(
            Api.deskTicket.getPresignedURL,
            file.type,
            dealerId
          );
          yield axios.put(uploadURL, file, {
            headers: { "Content-Type": file.type },
          });
          const urlObj = new URL(uploadURL);
          urlObj.search = "";
          return urlObj.toString();
        })
      );
      const dealerName = yield select(
        (state: RootState) =>
          state?.user?.selectedOrg?.["name"] ||
          state?.user?.selectedOrg?.associated_dealers?.[0]?.id ||
          "N/A"
      );
      const res = yield call(Api.deskTicket.contactTicket, {
        subject: `Dealer Portal Support Request - ${dealerName} - ${
          data?.help || "N/A"
        }`,
        description: generateHTMLContent(data, uploadedUrls),
        contact_email: contact_email || "no@email.com",
      });
      if (res.id) {
        callBack();
      }
    } catch (error) {
      console.log("error:", error);
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: `contact-${UserScope}`,
        error,
      });
    } finally {
      yield put(disableScopeLoading(`contact-${UserScope}`));
    }
  }
}

function* fetchContractRecordsFlow(): Generator<any, void, any> {
  while (true) {
    const { payload = {} } = yield take(UserAction.GET_CONTRACT_RECORDS);
    const { page = 1, per_page = 20, search = null } = payload;
    yield put(enableScopeLoading(`${UserScope}-contracts`));
    try {
      let oldData = [];
      let mappedData = [];
      let tempinfo = {};
      if (page === 1) {
        yield put(setContractRecords({ data: [], info: {} }));
      } else {
        oldData = yield select(
          (state: RootState) => state.user.contracts?.data || []
        );
      }
      if (search) {
        const { data = [], info = {} } = yield call(
          Api.contract.searchPlans,
          search,
          page,
          per_page
        );
        mappedData = data.map(mapApiContract);
        tempinfo = info;
      } else {
        const { data = [], info = {} } = yield call(
          Api.contract.getPlans,
          page,
          per_page
        );
        mappedData = data.map(mapApiContract);
        tempinfo = info;
      }

      const associatedDealers = yield select(
        (state: RootState) => state.user.selectedOrg.associated_dealers || []
      );
      const dealerMap = associatedDealers.reduce((acc: any, dealer: any) => {
        acc[dealer.id.toString()] = dealer.alias;
        return acc;
      }, {});

      mappedData = mappedData.map((contract: any) => ({
        ...contract,
        dealer_alias: dealerMap[contract.dealer_id] || "",
      }));

      yield put(
        setContractRecords({
          data: oldData.concat(mappedData),
          info: tempinfo,
        })
      );
    } catch (error) {
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: `${UserScope}-contracts`,
        error,
      });
    } finally {
      yield put(disableScopeLoading(`${UserScope}-contracts`));
    }
  }
}

function* fetchPendingContractRecordsFlow(): Generator<any, void, any> {
  while (true) {
    const { payload = {} } = yield take(
      UserAction.GET_PENDING_CONTRACT_RECORDS
    );
    const { page = 1, per_page = 20, search = null } = payload;
    yield put(enableScopeLoading(`${UserScope}-pendingContracts`));
    try {
      let oldData = [];
      if (page === 1) {
        yield put(setPendingContractRecords({ data: [], info: {} }));
      } else {
        oldData = yield select(
          (state: RootState) => state.user.pendingContracts?.data || []
        );
      }
      let mappedData = [];
      let tempinfo = {};
      if (search) {
        const { data = [], info = {} } = yield call(
          Api.contract.searchPendingContractRecords,
          search,
          page,
          per_page
        );
        mappedData = data.map(mapApiPendingContract);
        tempinfo = info;
      } else {
        const { data = [], info = {} } = yield call(
          Api.contract.getPendingContractRecords,
          page,
          per_page
        );
        mappedData = data.map(mapApiPendingContract);
        tempinfo = info;
      }
      yield put(
        setPendingContractRecords({
          data: oldData.concat(mappedData),
          info: tempinfo,
        })
      );
    } catch (error) {
      console.log("error:", error);
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: `${UserScope}-pendingContracts`,
        error,
      });
    } finally {
      yield put(disableScopeLoading(`${UserScope}-pendingContracts`));
    }
  }
}

function* fetchClaimRecordsFlow(): Generator<any, void, any> {
  while (true) {
    const { payload } = yield take(UserAction.GET_CLAIMS);
    const { page = 1, per_page = 20, search = null } = payload;
    yield put(enableScopeLoading(`${UserScope}-claims`));
    try {
      let oldData = [];
      let mappedData = [];
      let tempInfo = {};
      if (page === 1) {
        yield put(setClaims({ data: [], info: {} }));
      } else {
        oldData = yield select(
          (state: RootState) => state.user.claims.data || []
        );
      }
      if (search) {
        const { data = [], info = {} } = yield call(
          Api.claim.searchClaim,
          page,
          per_page,
          search
        );
        mappedData = data.map(mapApiClaim);
        tempInfo = info;
      } else {
        const { data = [], info = {} } = yield call(
          Api.claim.getAllClaimRecords,
          page,
          per_page
        );
        mappedData = data.map(mapApiClaim);
        tempInfo = info;
      }

      yield put(
        setClaims({
          data: oldData.concat(mappedData),
          info: tempInfo,
        })
      );
    } catch (error) {
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: `${UserScope}-claims`,
        error,
      });
    } finally {
      yield put(disableScopeLoading(`${UserScope}-claims`));
    }
  }
}

function* fetchSkusFlow(): Generator<any, void, any> {
  while (true) {
    const { payload: { deliveryDate = "", dealerId = "" } = {} } = yield take(
      UserAction.GET_SKUsOPTIONS
    );
    yield put(enableScopeLoading(`${UserScope}-skus`));
    try {
      const { data = [] } = yield call(
        Api.delerPlanoffering.getSkus,
        deliveryDate,
        dealerId
      );
      yield put(setSkus(data.map(mapApiSkus)));
    } catch (error) {
      yield put(setSkus([]));
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: `${UserScope}-skus`,
        error,
      });
    } finally {
      yield put(disableScopeLoading(`${UserScope}-skus`));
    }
  }
}

export default function* userSaga(): Generator<any, void, any> {
  yield fork(submitTicketFlow);
  yield fork(fetchContractRecordsFlow);
  yield fork(fetchPendingContractRecordsFlow);
  yield fork(fetchSkusFlow);
  yield fork(fetchClaimRecordsFlow);
}
